import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PhotoIcon } from '@heroicons/react/24/outline';
import { Callout, Cluster, ColorSwatch, FileButton, Form, FormActions, FormError, FormField, Input, PageHeader, Select, Skeleton, useZodForm } from '../components/ui';
import { useAuth } from '../lib/auth';
import { useCapabilities } from '../lib/capabilities';
import { errorMessage } from '../lib/errorMessage';
import {
  getOrgBranding,
  setOrgBranding,
  uploadOrgBrandingLogo,
  uploadOrgBrandingFavicon,
  orgBrandingKeys,
  SetOrgBrandingBodySchema,
  type SetOrgBrandingBody,
} from '../lib/api/orgBranding.gen';

// Story 6-9 — labels for the curated brand-font enum (contracts/orgBranding.js's
// header has the full "why these three" argument). Kept here, next to the
// only UI that renders them, rather than in the generated client.
const FONT_LABELS: Record<'outfit' | 'system' | 'serif', string> = {
  outfit: 'Outfit (modern, geometric)',
  system: 'System default (native, fastest)',
  serif: 'Serif (formal, traditional)',
};

// Settings→Branding (story 6-8, P10 §1 Layer 2 / §3). The org superadmin sets
// the two allow-listed overrides P10 §1 names (`--primary`, logo) plus a
// display-name override for reports/portal. `GatedRoute` (App.tsx) already
// renders the locked teaser for a free org and PERMISSION_DENIED for a Pro
// user without `org:branding:manage` — this component only ever mounts for a
// principal who may actually use it, so it renders the real editor
// unconditionally rather than re-deriving that gate.
//
// G-26: the form's schema is `SetOrgBrandingBodySchema`, imported verbatim
// from the generated client (`lib/api/orgBranding.gen.ts`) — never hand-
// declared here. The logo is NOT a form field (there is no `logoUrl` body
// field to bind — contracts/orgBranding.js's own header explains why): it
// uploads immediately on file pick via the new `FileButton` DS primitive
// (G-22: composed here, not a raw `<input type="file">` one-off) — see
// `FileButton.tsx`'s own header for why this needed a new, smaller primitive
// rather than reusing `PhotoInput` (evidence-oriented: multi-photo, per-item
// progress/retry/caption state, a shape one named org resource doesn't fit).
export function OrgBrandingPage(): JSX.Element {
  const { user } = useAuth();
  const { refetch: refetchCapabilities } = useCapabilities();
  const queryClient = useQueryClient();
  const orgId = user?.organizationId ?? '';
  const [saveError, setSaveError] = useState<string | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [faviconError, setFaviconError] = useState<string | null>(null);

  const brandingQuery = useQuery({
    queryKey: orgBrandingKeys.detail(orgId),
    queryFn: () => getOrgBranding(orgId).then((r) => r.data),
    enabled: Boolean(orgId),
  });

  const form = useZodForm(SetOrgBrandingBodySchema, {
    defaultValues: { organizationName: null, primaryColor: null, accentColor: null, font: null },
  });

  // Populate the form once the current branding loads — an edit form over
  // async data, not a create form with static defaults.
  useEffect(() => {
    if (!brandingQuery.data) return;
    form.reset({
      organizationName: brandingQuery.data.organizationName,
      primaryColor: brandingQuery.data.primaryColor,
      accentColor: brandingQuery.data.accentColor,
      font: brandingQuery.data.font,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandingQuery.data]);

  const save = useMutation({
    mutationFn: (body: SetOrgBrandingBody) => setOrgBranding(orgId, body),
    onSuccess: async (res) => {
      setSaveError(null);
      queryClient.setQueryData(orgBrandingKeys.detail(orgId), res.data);
      // P10 §3 "Save → SSE → soft re-theme" (orgBranding.service#setBranding
      // already pushed capabilities.changed); THIS tab refetches immediately
      // rather than waiting on its own SSE round-trip.
      await refetchCapabilities();
    },
    onError: (e) => setSaveError(errorMessage(e)),
  });

  const uploadLogo = useMutation({
    mutationFn: (file: File) => uploadOrgBrandingLogo(orgId, {}, file),
    onSuccess: async (res) => {
      setLogoError(null);
      queryClient.setQueryData(orgBrandingKeys.detail(orgId), res.data);
      await refetchCapabilities();
    },
    onError: (e) => setLogoError(errorMessage(e)),
  });

  // Story 6-9 — same immediate-upload-on-pick shape as uploadLogo above.
  const uploadFavicon = useMutation({
    mutationFn: (file: File) => uploadOrgBrandingFavicon(orgId, {}, file),
    onSuccess: async (res) => {
      setFaviconError(null);
      queryClient.setQueryData(orgBrandingKeys.detail(orgId), res.data);
      await refetchCapabilities();
    },
    onError: (e) => setFaviconError(errorMessage(e)),
  });

  if (brandingQuery.isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Branding" description="Apply your brand across reports and the portal." />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const currentLogoUrl = uploadLogo.data?.data.logoUrl ?? brandingQuery.data?.logoUrl ?? null;
  const currentFaviconUrl = uploadFavicon.data?.data.faviconUrl ?? brandingQuery.data?.faviconUrl ?? null;
  const currentForeground = brandingQuery.data?.primaryForeground ?? null;
  const currentAccentForeground = brandingQuery.data?.accentForeground ?? null;

  return (
    <div className="max-w-2xl space-y-6">
      <PageHeader title="Branding" description="Apply your brand across reports and the portal." />

      <section className="space-y-3 rounded-hairline border border-border bg-card p-5">
        <h2 className="text-sm font-semibold text-foreground">Logo</h2>
        <p className="text-xs text-muted-foreground">PNG, JPEG, WebP or SVG, up to 2 MB. Shown on reports and the client portal.</p>
        <Cluster gap={4}>
          <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-muted">
            {currentLogoUrl ? (
              <img src={currentLogoUrl} alt="Organization logo" className="h-full w-full object-contain" />
            ) : (
              <PhotoIcon className="h-6 w-6 text-muted-foreground" aria-hidden />
            )}
          </div>
          <div className="space-y-1">
            <FileButton
              variant="secondary"
              size="sm"
              disabled={uploadLogo.isPending}
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              inputLabel="Upload organization logo"
              onSelect={(file) => uploadLogo.mutate(file)}
            >
              {uploadLogo.isPending ? 'Uploading…' : currentLogoUrl ? 'Replace logo' : 'Upload logo'}
            </FileButton>
          </div>
        </Cluster>
        <FormError message={logoError} />
      </section>

      {/* Story 6-9 — same immediate-upload-on-pick shape as the Logo section
          above, its own storage/size budget (see uploadOrgBrandingFavicon's
          own contract summary for why this is a separate upload from the logo). */}
      <section className="space-y-3 rounded-hairline border border-border bg-card p-5">
        <h2 className="text-sm font-semibold text-foreground">Favicon</h2>
        <p className="text-xs text-muted-foreground">PNG, SVG, WebP or ICO, up to 512 KB. Shown as the browser-tab icon.</p>
        <Cluster gap={4}>
          <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-border bg-muted">
            {currentFaviconUrl ? (
              <img src={currentFaviconUrl} alt="Organization favicon" className="h-full w-full object-contain" />
            ) : (
              <PhotoIcon className="h-6 w-6 text-muted-foreground" aria-hidden />
            )}
          </div>
          <div className="space-y-1">
            <FileButton
              variant="secondary"
              size="sm"
              disabled={uploadFavicon.isPending}
              accept="image/png,image/svg+xml,image/webp,image/x-icon,image/vnd.microsoft.icon"
              inputLabel="Upload organization favicon"
              onSelect={(file) => uploadFavicon.mutate(file)}
            >
              {uploadFavicon.isPending ? 'Uploading…' : currentFaviconUrl ? 'Replace favicon' : 'Upload favicon'}
            </FileButton>
          </div>
        </Cluster>
        <FormError message={faviconError} />
      </section>

      <Form form={form} onSubmit={(values) => save.mutate(values)} className="space-y-4 rounded-hairline border border-border bg-card p-5">
        <h2 className="text-sm font-semibold text-foreground">Details</h2>
        <FormField name="organizationName" label="Display name" description="Shown on reports and the portal instead of your account's organization name. Leave blank to use the account name.">
          {(field) => (
            <Input
              {...field}
              type="text"
              placeholder="Acme Inspections"
              // Same "empty means clear, stored as null" rule as primaryColor below.
              onChange={(event) => field.onChange(event.target.value === '' ? null : event.target.value)}
            />
          )}
        </FormField>

        <FormField
          name="primaryColor"
          label="Primary color"
          description={
            currentForeground
              ? `A 6-digit hex color, checked for WCAG AA contrast when you save. Button text on this color is currently ${currentForeground}.`
              : 'A 6-digit hex color, checked for WCAG AA contrast when you save.'
          }
        >
          {(field) => (
            <Input
              {...field}
              type="text"
              placeholder="#1263D3"
              // Empty input means "clear the color" — stored as `null`
              // (SetOrgBrandingBodySchema's own shape), never `''` (which
              // would fail its `.nullable()` schema's hex regex on submit).
              onChange={(event) => field.onChange(event.target.value === '' ? null : event.target.value)}
              // ALWAYS an element (never `undefined`) — `Input.tsx`'s
              // `hasAdornment` picks between two different DOM structures
              // (bare `<input>` vs a wrapper `<span>`), so toggling this prop
              // between an element and `undefined` AS THE USER TYPES (every
              // keystroke changes whether the value is a full valid hex)
              // unmounts and remounts the real `<input>` mid-keystroke,
              // dropping focus and every character after the first — found
              // live via browser automation typing "#6a1b9a" and observing
              // only "#" land. The swatch's OWN visibility is controlled
              // inside the always-present element instead (transparent
              // background until the value is a full valid hex).
              adornmentEnd={
                <ColorSwatch
                  className="h-4 w-4"
                  color={
                    typeof field.value === 'string' && /^#[0-9a-fA-F]{6}$/.test(field.value) ? field.value : undefined
                  }
                />
              }
            />
          )}
        </FormField>

        {/* Story 6-9 — the "portal accent" role (P10 §1; see
            contracts/orgBranding.js's header for the full --accent mapping
            argument). Byte-identical field shape to primaryColor above —
            same empty-clears-to-null rule, same focus-preserving
            always-present-element adornment, same save-time WCAG AA gate. */}
        <FormField
          name="accentColor"
          label="Accent color"
          description={
            currentAccentForeground
              ? `A second brand color for portal/report accents, checked for WCAG AA contrast when you save. Text on this color is currently ${currentAccentForeground}.`
              : 'A second brand color for portal/report accents, checked for WCAG AA contrast when you save.'
          }
        >
          {(field) => (
            <Input
              {...field}
              type="text"
              placeholder="#7C3AED"
              onChange={(event) => field.onChange(event.target.value === '' ? null : event.target.value)}
              adornmentEnd={
                <ColorSwatch
                  className="h-4 w-4"
                  color={
                    typeof field.value === 'string' && /^#[0-9a-fA-F]{6}$/.test(field.value) ? field.value : undefined
                  }
                />
              }
            />
          )}
        </FormField>

        {/* Story 6-9 — curated, allow-listed brand-font selection (contracts/
            orgBranding.js's header has the full "why these three, why no
            upload" argument). A real Select, not a free-text field — the
            enum IS the allow-list. */}
        <FormField name="font" label="Font" description="Applied to headings and body text app-wide, and in the header of shared reports.">
          {(field) => (
            <Select
              {...field}
              value={field.value ?? ''}
              onChange={(event) => field.onChange(event.target.value === '' ? null : event.target.value)}
              placeholder="App default (Plus Jakarta Sans / Outfit)"
            >
              {(['outfit', 'system', 'serif'] as const).map((key) => (
                <option key={key} value={key}>
                  {FONT_LABELS[key]}
                </option>
              ))}
            </Select>
          )}
        </FormField>

        <FormError message={saveError} />

        {/* 2-147we: converged onto `FormActions` (2-147ua widened this
            host to "every real dialog/page form that already composed
            Form/FormField") — a single `type: 'submit'` entry renders the
            same `FormSubmit` this page used directly, now through the
            governed host instead of a bare call. */}
        <FormActions
          actions={[
            { id: 'save', type: 'submit', label: 'Save changes', pendingLabel: 'Saving…', loading: save.isPending, variant: 'primary' },
          ]}
        />
      </Form>

      {brandingQuery.isError && (
        <Callout tone="warning" title="Could not load branding">
          {errorMessage(brandingQuery.error)}
        </Callout>
      )}
    </div>
  );
}
